import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ONBOARDING_STEPS } from "./onboarding.config";
import { brand, colors } from "../../Theme/ColorsForTheam";

// Left-pointing arrow (toward the sidebar the step highlights).
const arrowLeftSx = {
  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: -8,
    transform: "translateY(-50%)",
    width: 0,
    height: 0,
    borderTop: "8px solid transparent",
    borderBottom: "8px solid transparent",
    borderRight: `8px solid ${brand.surface}`,
  },
};

const eyebrowSx = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.8,
  textTransform: "uppercase" as const,
  color: brand.orange,
  background: brand.tint50,
  border: `1px solid ${brand.tint100}`,
  borderRadius: "999px",
  px: 1.2,
  py: 0.3,
};

const primaryBtnSx = {
  background: brand.orange,
  color: "#fff",
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: 13,
  borderRadius: "12px",
  px: 2.5,
  py: 0.8,
  boxShadow: brand.shadowOrange,
  "&:hover": { background: brand.accentHover },
};

const ghostBtnSx = {
  color: colors.gray[400],
  textTransform: "none" as const,
  fontSize: 12,
  fontWeight: 500,
};

interface Props {
  open: boolean;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onClose: () => void;
  onComplete: () => void;
}

/**
 * Onboarding guide card — points at the step's sidebar item.
 *
 * Deliberately a plain fixed-position card whose coordinates we measure ourselves, NOT a MUI Popover
 * anchored to the DOM node. The anchored Popover kept repositioning against a still-laying-out element
 * on a slow first load and drove its Grow transition into an infinite setState loop (React #185).
 * Here there is no transition and no continuous repositioning — just one setState with the measured
 * coordinates per step — so that class of crash is impossible.
 */
export const OnboardingPopover = ({
  open,
  currentStep,
  totalSteps,
  onNext,
  onClose,
  onComplete,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const [visible, setVisible] = React.useState(false);
  // Mirrors `visible` for the layout effect: it needs to know whether the card was already on screen
  // (step change → fade out first) or not (first open → position right away).
  const visibleRef = React.useRef(false);

  const step = ONBOARDING_STEPS[currentStep];
  const isLast = currentStep === totalSteps - 1;

  // Navigate to the step's route when the step changes.
  React.useEffect(() => {
    if (open && step?.route) navigate(step.route);
  }, [open, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pin the card to the step's sidebar item, and only reveal it once that position has settled.
  //
  // The sidebar section expands with an animation, so the item slides for a few hundred ms after a
  // step change. We hide the card while that happens, keep re-reading the position (invisibly), and
  // fade it in once the item has stopped moving — instead of letting the card visibly race after it.
  React.useLayoutEffect(() => {
    if (!open || !step) {
      setPos(null);
      setVisible(false);
      visibleRef.current = false;
      return;
    }

    // Hide first, and crucially do NOT touch `pos` yet: the card fades out where it already is.
    // Moving it in the same commit made it teleport to the new spot while still visible, which read
    // as "show → hide → show".
    const wasVisible = visibleRef.current;
    setVisible(false);
    visibleRef.current = false;

    const FADE_MS = 240; // must cover the CSS opacity transition below
    const MAX_TICKS = 20; // ~2s cap — show anyway rather than get stuck
    const STABLE_TICKS = 3; // position unchanged for ~300ms → the menu finished opening
    let cancelled = false;
    let ticks = 0;
    let stable = 0;
    let last: { top: number; left: number } | null = null;
    let found = false;
    let timer: ReturnType<typeof setTimeout>;

    const clampTop = (y: number) => Math.min(Math.max(y, 120), window.innerHeight - 120);

    const read = (): { top: number; left: number } | null => {
      const el = document.querySelector(`[data-onboarding="${step.anchor}"]`) as HTMLElement | null;
      const rect = el?.getBoundingClientRect();
      if (!el || !rect || rect.height === 0) return null; // not laid out yet — a later tick catches it
      return { top: clampTop(rect.top + rect.height / 2), left: rect.right + 16 };
    };

    // Only update when it actually moved, so re-reads cost nothing once it settles.
    const apply = (p: { top: number; left: number }) =>
      setPos((prev) => (prev && prev.top === p.top && prev.left === p.left ? prev : p));

    const show = () => {
      setVisible(true);
      visibleRef.current = true;
    };

    const tick = () => {
      if (cancelled) return;
      const p = read();
      if (p) {
        found = true;
        apply(p);
        stable = last && last.top === p.top && last.left === p.left ? stable + 1 : 0;
        last = p;
      }
      ticks++;

      if (found && stable >= STABLE_TICKS) {
        show(); // settled → fade in at the final spot
        return;
      }
      if (ticks <= MAX_TICKS) {
        timer = setTimeout(tick, 100);
        return;
      }
      if (!found) {
        // Item never showed up — park the card beside the sidebar so the guide can't get stuck.
        const w = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--g-sidebar-w'), 10);
        setPos({ top: clampTop(window.innerHeight / 2), left: (Number.isFinite(w) ? w : 240) + 16 });
      }
      show();
    };

    const onResize = () => {
      const p = read();
      if (p) apply(p);
    };

    if (wasVisible) {
      // Let it fade out where it stands, then reposition + fade in — never move it while on screen.
      timer = setTimeout(tick, FADE_MS);
    } else {
      tick(); // nothing on screen yet — measure right away, before the first paint
    }
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, [open, currentStep, step]);

  // Render only once positioned — avoids a flash at a placeholder spot followed by a jump.
  if (!open || !step || !pos) return null;

  const handleOk = () => {
    if (isLast) onComplete();
    else onNext();
  };

  return (
    <>
      {/* Dimmed backdrop so the card reads as a focused step. No blur on purpose — backdrop-filter
          blurs the whole page behind it (the sidebar included), which made the menu the guide points
          at unreadable. A plain dim keeps everything legible. Click it to dismiss the guide, matching
          the old popover's click-outside-to-close behaviour. Fades together with the card. */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1299,
          bgcolor: "var(--g-overlay)",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
          transition: "opacity 220ms ease",
        }}
      />
      <Box
      sx={{
        position: "fixed",
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        transform: "translateY(-50%)",
        zIndex: 1300,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "auto" : "none",
        // ONLY opacity animates: position changes apply instantly while the card is hidden, so it
        // never visibly slides around as the menu opens. Plain CSS on purpose — a react-transition-group
        // transition (MUI Fade/Grow) is exactly what looped into React #185 here before.
        transition: "opacity 220ms ease",
        width: 300,
        p: 2.5,
        borderRadius: "18px",
        border: `1px solid ${brand.border}`,
        background: brand.surface,
        boxShadow: "var(--g-shadow-lg)",
        ...arrowLeftSx,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
        <Typography sx={eyebrowSx}>
          {currentStep + 1} / {totalSteps}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 700, color: brand.dark, mb: 0.8 }}>
        {t(step.titleKey)}
      </Typography>

      <Typography sx={{ fontSize: 13, lineHeight: 1.65, color: colors.gray[600], mb: 2.5 }}>
        {t(step.descriptionKey)}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button size="small" onClick={onClose} sx={ghostBtnSx}>
          {t("Onboarding.skip")}
        </Button>
        <Button size="small" variant="contained" onClick={handleOk} disableElevation sx={primaryBtnSx}>
          {isLast ? t("Onboarding.finish") : t("Onboarding.ok")}
        </Button>
        </Box>
      </Box>
    </>
  );
};
